import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useSyncExternalStore, type FormEvent } from 'react';
import { phraseKey } from '@app/shared/place-rules';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { Notice } from '../ui/Notice';
import { PLACE_RULES_KEY, placeLabel, type PlaceRule, type PlaceValue } from '../settings/placeDefaults';
import { SETTINGS_RESOURCE } from '../settings/company';
import { rememberOffer, type AutoState } from './autoPlace';
import './fields.css';

/**
 * „Zapamiętać: „PFK” → Sopot, Mamuszki 14?” (owner 2026-10-30 22:23: rules „ktore sie same wypelniaja”): after a new
 * event is saved with a place set by hand, a green notice at the bottom (#205) offers to keep it as a rule of
 * „Skojarzenia miejsc”. The phrase can be changed first; a rule is made only by „Zapamiętaj”, never by itself. The
 * form is gone by then (the panel shows the new card), so the offer lives here, outside it.
 */
type Offer = { id: number; phrase: string; place: PlaceValue; rules: PlaceRule[] };

let offer: Offer | null = null;
let counter = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/**
 * After saving a new event: ask, when there is something to ask and the person may change the rules. A question left
 * from an event before goes — it was about that one.
 */
export function offerPlaceRule(me: Me | undefined, name: string, place: PlaceValue, state: AutoState, rules: PlaceRule[]): void {
  const asked = me && can(me, SETTINGS_RESOURCE, 'edit') ? rememberOffer(name, place, state, rules) : null;
  if (!asked && !offer) return;
  offer = asked ? { id: ++counter, ...asked, rules } : null;
  emit();
}

const dismiss = (id: number) => {
  if (offer?.id !== id) return;
  offer = null;
  emit();
};

function Ask({ current }: { current: Offer }) {
  const queryClient = useQueryClient();
  const [phrase, setPhrase] = useState(current.phrase);
  const [done, setDone] = useState<string | null>(null);
  const label = placeLabel(current.place);
  const save = useMutation({
    mutationFn: () => {
      // the same phrase as a rule there is: that rule gets the new place
      const same = current.rules.find((r) => phraseKey(r.phrase) === phraseKey(phrase));
      return same
        ? api(`/api/events/place-rules/${same.id}`, { method: 'PATCH', body: { version: same.version, phrase: same.phrase, place: current.place } })
        : api('/api/events/place-rules', { method: 'POST', body: { phrase: phrase.trim(), place: current.place } });
    },
    onSuccess: async () => {
      setDone(`Zapamiętano: „${phrase.trim()}” → ${label}.`);
      await queryClient.invalidateQueries({ queryKey: PLACE_RULES_KEY });
    },
  });
  if (done) return <Notice onClose={() => dismiss(current.id)}>{done}</Notice>;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (phraseKey(phrase) !== '' && !save.isPending) save.mutate();
  };
  const error = save.error instanceof ApiError ? fieldIssues(save.error).map((i) => i.message).join(' ') || save.error.message : save.error ? 'Nie udało się zapisać' : '';
  return (
    <Notice onClose={() => dismiss(current.id)} data-feature-id="events.place_defaults">
      <form className="remember-rule" aria-label="Zapamiętać skojarzenie miejsca" onSubmit={submit}>
        <span>{`Zapamiętać: „${phrase.trim()}” → ${label}?`}</span>
        <input aria-label="Fraza" className="remember-rule-input" value={phrase} maxLength={100} onChange={(e) => setPhrase(e.target.value)} autoComplete="off" />
        <button className="btn small primary" type="submit" disabled={save.isPending || phraseKey(phrase) === ''}>
          {save.isPending ? 'Zapisywanie…' : 'Zapamiętaj'}
        </button>
        {error && (
          <span className="field-error" role="alert">
            {error}
          </span>
        )}
      </form>
    </Notice>
  );
}

/** Where the offer shows: once on the events screen (and in tests beside the form). */
export function RememberRuleHost() {
  const current = useSyncExternalStore(subscribe, () => offer, () => null);
  return current ? <Ask key={current.id} current={current} /> : null;
}
